/**
 * The Builder: start a map by choosing how it is arranged.
 *
 * Every layout this build can run, grouped by family, each drawn as a real little map by the same
 * renderer the editor uses — so a card is a promise the editor keeps. The featured row comes
 * first: one of each kind, the choices that differ most, so the range is visible before anybody
 * scrolls. Choosing is a radio group; creating is a separate press, with a name if you have one
 * and "Skip" if you do not care yet.
 */
import { useState } from 'react';

import { type LayoutId } from '@orbit-mind/shared-types';
import { usePreferences } from '@orbit-mind/engine-react';

import { Button } from '../ui/controls';
import { LayoutGalleryGrid } from './LayoutGalleryGrid';

export function BuilderPanel({
  busy,
  onCreate,
}: {
  busy: boolean;
  /** Creates a blank map arranged by `layoutId`, named `title` (or the default when empty). */
  onCreate: (layoutId: LayoutId, title: string) => void;
}): React.JSX.Element {
  const preferences = usePreferences();
  const [chosen, setChosen] = useState<LayoutId>(preferences.layout);
  const [title, setTitle] = useState('');

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4">
        <LayoutGalleryGrid chosen={chosen} onChoose={setChosen} />
      </div>

      <form
        className="border-edge flex shrink-0 flex-wrap items-center gap-2 border-t px-3 py-3 sm:px-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) onCreate(chosen, title.trim());
        }}
      >
        <input
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          placeholder="Name your map (optional)"
          aria-label="Map name"
          data-create-blank
          className="border-edge bg-surface text-ink focus:border-edge-strong h-10 min-w-0 flex-1 rounded-lg border px-3 text-sm outline-none"
        />
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            onCreate(preferences.layout, title.trim());
          }}
        >
          Skip
        </Button>
        <Button tone="primary" type="submit" disabled={busy}>
          Create map
        </Button>
      </form>
    </div>
  );
}
